import axios from 'axios';
import React, { ChangeEvent, FC, MouseEvent, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import BackLink from '../shared/BackLink';
import { AlertText, Header, PrimaryButton, Text, Wrapper } from '../v3Styles';
import Checkbox from './Checkbox';
import { Box, BoxWrapper, CheckboxWrapper } from './unsubStyles';

interface UnsubscribeRequest {
  email: string | null;
  sourceEventId: string | null;
  all: true;
  reasons: string[];
  otherReason?: string;
}

const UnsubscribeReasons: FC = () => {
  const history = useHistory();
  const location = useLocation();
  const [noLongerSchoolCheck, setNoLongerSchoolCheck] = useState(false);
  const [noLongerBuyCheck, setNoLongerBuyCheck] = useState(false);
  const [tooManyEmailsCheck, setTooManyEmailsCheck] = useState(false);
  const [doNotWantEmailsCheck, setDoNotWantEmailsCheck] = useState(false);
  const [receiveFromSchoolCheck, setReceiveFromSchoolCheck] = useState(false);
  const [otherCheck, setOtherCheck] = useState(false);
  const [otherReason, setOtherReason] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const query = new URLSearchParams(location.search);
  const email = query.get('email');
  const eventId = query.get('eventId');

  const handleSubmit = async () => {
    const reasons: string[] = [];
    if (noLongerSchoolCheck) {
      reasons.push('no_longer_attends_school');
    }
    if (noLongerBuyCheck) {
      reasons.push('no_longer_buy_portraits');
    }
    if (tooManyEmailsCheck) {
      reasons.push('too_many_emails');
    }
    if (doNotWantEmailsCheck) {
      reasons.push('do_not_want_emails');
    }
    if (receiveFromSchoolCheck) {
      reasons.push('receive_info_directly_from_school');
    }
    // The reasons are optional feedback. An unsubscribe with none must still
    // go through, so an empty list is sent as it is.
    const body: UnsubscribeRequest = {
      email,
      sourceEventId: eventId,
      all: true,
      reasons,
    };
    if (otherCheck && otherReason) {
      body.otherReason = otherReason;
    }
    setErrorMessage('');
    try {
      await axios.post('/api/v2/parents/unsubscribe-email', body);
      history.push('/unsubscribe-message');
    } catch (error) {
      console.error(error);
      setErrorMessage("Sorry, we couldn't unsubscribe you. Please try again.");
    }
  };

  const handleNoLongerSchoolCheck = (e: MouseEvent<HTMLLabelElement>) => {
    e.preventDefault();
    setNoLongerSchoolCheck(!noLongerSchoolCheck);
  };

  const handleNoLongerBuyCheck = (e: MouseEvent<HTMLLabelElement>) => {
    e.preventDefault();
    setNoLongerBuyCheck(!noLongerBuyCheck);
  };

  const handleTooManyEmailsCheck = (e: MouseEvent<HTMLLabelElement>) => {
    e.preventDefault();
    setTooManyEmailsCheck(!tooManyEmailsCheck);
  };

  const handleDoNotWantEmailsCheck = (e: MouseEvent<HTMLLabelElement>) => {
    e.preventDefault();
    setDoNotWantEmailsCheck(!doNotWantEmailsCheck);
  };

  const handleReceiveFromSchoolCheck = (e: MouseEvent<HTMLLabelElement>) => {
    e.preventDefault();
    setReceiveFromSchoolCheck(!receiveFromSchoolCheck);
  };

  const handleOtherCheck = (e: MouseEvent<HTMLLabelElement>) => {
    e.preventDefault();
    setOtherCheck(!otherCheck);
  };

  const handleOtherReasonChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setOtherReason(e.currentTarget.value);
  };

  return (
    <Wrapper>
      <BackLink />
      <Wrapper>
        <Header>
          Please check the reason you are unsubscribing from all emails below.
        </Header>
        <Text>
          We'd greatly appreciate your feedback on why you are unsubscribing so
          we can improve our service
        </Text>
        <Wrapper>
          <BoxWrapper>
            <Box>
              <CheckboxWrapper onClick={handleNoLongerSchoolCheck}>
                My child no longer attends this school
                <Checkbox checked={noLongerSchoolCheck} id="reason-1" />
              </CheckboxWrapper>
            </Box>
          </BoxWrapper>
          <BoxWrapper>
            <Box>
              <CheckboxWrapper onClick={handleNoLongerBuyCheck}>
                I no longer buy school portraits anymore
                <Checkbox checked={noLongerBuyCheck} id="reason-2" />
              </CheckboxWrapper>
            </Box>
          </BoxWrapper>
          <BoxWrapper>
            <Box>
              <CheckboxWrapper onClick={handleTooManyEmailsCheck}>
                I receive too many emails
                <Checkbox checked={tooManyEmailsCheck} id="reason-3" />
              </CheckboxWrapper>
            </Box>
          </BoxWrapper>
          <BoxWrapper>
            <Box>
              <CheckboxWrapper onClick={handleDoNotWantEmailsCheck}>
                I don't want to receive emails about this
                <Checkbox checked={doNotWantEmailsCheck} id="reason-4" />
              </CheckboxWrapper>
            </Box>
          </BoxWrapper>
          <BoxWrapper>
            <Box>
              <CheckboxWrapper onClick={handleReceiveFromSchoolCheck}>
                I receive this information from my child's school directly
                <Checkbox checked={receiveFromSchoolCheck} id="reason-5" />
              </CheckboxWrapper>
            </Box>
          </BoxWrapper>
          <BoxWrapper>
            <Box>
              <CheckboxWrapper onClick={handleOtherCheck}>
                Other
                <Checkbox checked={otherCheck} id="reason-6" />
              </CheckboxWrapper>
              <textarea
                onChange={handleOtherReasonChange}
                placeholder="Please write your reason here..."
                value={otherReason}
                rows={5}
              />
            </Box>
          </BoxWrapper>
          {errorMessage && <AlertText role="alert">{errorMessage}</AlertText>}
          <PrimaryButton onClick={handleSubmit}>
            Submit & Unsubscribe
          </PrimaryButton>
        </Wrapper>
      </Wrapper>
    </Wrapper>
  );
};

export default UnsubscribeReasons;
